import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, Send } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { gradientText, gradientButton } from '../theme';

function ForgotPassword() {
  const { t } = useTranslation();
  const { resetPassword } = useAuthContext();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const validateForm = () => {
    const newErrors: { email?: string } = {};

    if (!email) {
      newErrors.email = t('auth.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('auth.validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isLoading) return;

    setIsLoading(true);
    setAuthError(null);

    const { error } = await resetPassword(email);
    
    if (error) {
      setAuthError(t('auth.forgotPassword.resetLinkError'));
      setIsLoading(false);
      return;
    }

    setIsSubmitted(true);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link
          to="/login"
          className={`inline-flex items-center gap-2 ${gradientText} hover:opacity-80 transition-opacity mb-8`}
        >
          <ArrowLeft className="w-5 h-5" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>

        <h2 className={`text-center text-3xl font-bold ${gradientText}`}>
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400">
          {t('auth.forgotPassword.instructions')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-[#0A0A0A] py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {authError && (
            <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
              {authError}
            </div>
          )}

          {isSubmitted ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-start/20 to-primary-end/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-primary-end" />
              </div>
              <h3 className={`text-xl font-semibold ${gradientText} mb-3`}>
                {t('auth.forgotPassword.emailSentTitle')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('auth.forgotPassword.emailSentMessage')}
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full bg-black border ${
                      errors.email ? 'border-red-500' : 'border-gray-800'
                    } rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:border-primary-end text-white`}
                    placeholder={t('auth.forgotPassword.email')}
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${gradientButton} text-white font-medium py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {t('auth.forgotPassword.submit')}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;